import { useEffect, useReducer } from 'react'
import { useIntl } from 'react-intl'
import { connect, useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import { useToken } from '@hooks'
import { Dispatch, RootState } from '@stores'
import { TPayFormState } from '@stores/types/*'
import styled from 'styled-components'

import { Color } from '../../../../generated/quicktype/Tokens'
import { commonApplicationClient } from '../../../api/clients/common-application-client'
import { useInit } from '../../../hooks/useInit'
import { payFormService, personalDataService } from '../../../service/payform'
import {
  applicationReducer,
  ApplicationReducerActions,
  TApplicationState,
} from '../../../stores/reducers/ApplicationReducer'
import { TPaymentGatewayState } from '../../../stores/types/TPaymentGatewayState'
import { H3Weight600 } from '../../common/header/Headings'
import { Outlet } from '../../common/Outlet'

const Header = styled.div<Color>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  background-color: ${(color) => color.bg.page.value};
`

const IslandBlock = styled.div`
  display: flex;
  width: 100%;
  padding: 0 24px 0;
  margin-top: 20px;
  margin-bottom: 12px;
  font-weight: 600;
`

interface StateProps {
  payForm: TPayFormState
  paymentGateways: TPaymentGatewayState
}

type Props = StateProps

interface PersonalDataReducerAction {
  userId: string
  paymentGatewayId: string
}

interface PersonalDataState {
  fetched: boolean
}

const personalDataReducer = (
  state: PersonalDataState,
  action: PersonalDataReducerAction,
) => {
  if (!state.fetched) {
    commonApplicationClient
      .personalData(action.userId, action.paymentGatewayId)
      .then((res) => personalDataService.init(res))
    return { fetched: true }
  }
  return state
}

export const PurchaseWrapperPage = (props: Props) => {
  const intl = useIntl()
  const dispatch = useDispatch<Dispatch>()
  const initialApplication: TApplicationState = { applicationId: '' }
  const initialPersonalData = { fetched: false }

  const applicationId = useParams().applicationId
  const [, applicationDispatch] = useReducer(
    applicationReducer,
    initialApplication,
  )
  const [, personalDataDispatch] = useReducer(
    personalDataReducer,
    initialPersonalData,
  )

  const paymentGateways = useSelector(
    (state: RootState) => state.paymentGateways?.paymentGateways,
  )
  useInit(async () => {
    if (!applicationId || applicationId === 'undefined') {
      return
    } else if (!paymentGateways) {
      await Promise.all([
        dispatch.paymentGateways.fetchPaymentGateways(applicationId),
      ])
    }
  })

  useEffect(() => {
    if (!applicationId || applicationId === 'undefined') {
      return
    } else {
      applicationDispatch({
        action: ApplicationReducerActions.GET,
        applicationId: applicationId,
      })
    }
  }, [applicationId])

  useEffect(() => {
    if (props.payForm.application?.userId && props.payForm.paymentGateway) {
      personalDataDispatch({
        userId: props.payForm.application?.userId,
        paymentGatewayId: props.payForm.paymentGateway?.paymentGatewayId,
      })
    }
  }, [
    props.payForm.application?.userId,
    props.payForm.paymentGateway?.paymentGatewayId,
  ])

  useEffect(() => {
    document.title = intl.formatMessage({ id: 'purchase.title' })
  }, [])

  useEffect(() => {
    if (props.paymentGateways?.paymentGateways) {
      payFormService.initBySession(props.paymentGateways.paymentGateways)
    }
  }, [props.paymentGateways])

  return (
    <>
      <Header {...useToken().color} data-testid="purchase.header">
        <IslandBlock>
          <H3Weight600>
            {intl.formatMessage({ id: 'purchase.title2' })}
          </H3Weight600>
        </IslandBlock>
      </Header>
      <Outlet />
    </>
  )
}

const mapStateToProps = (state: RootState): StateProps => ({
  payForm: state.payForm,
  paymentGateways: state.paymentGateways,
})

export default connect(mapStateToProps)(PurchaseWrapperPage)
